import React, { useEffect, useState } from "react";
import xml2js from "xml2js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import classNames from "classnames";
import { createMulesoftEndpoint, fetchMulesoftEndpoints } from "./index";
import { EMPTY_ARRAY, EMPTY_STRING, INDEX, ACE_EDITOR_THEMES } from "../../constants";
import { addImageIcon, chevronLeftIcon, pasteIcon } from "../../assets/icons";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import AceEditor from "react-ace";
import Beautify from "ace-builds/src-noconflict/ext-beautify";
import "ace-builds/src-noconflict/mode-xml";
import "ace-builds/src-noconflict/theme-twilight";
import "ace-builds/src-noconflict/ext-language_tools";
import { selectAlert } from "../../components/organisms/Alert";
import { actions as mulesoftActions } from "./mulesoft.reducers";
import { selectSelectedIntegration } from "./mulesoft.selectors";
import  'ace-builds/src-noconflict/ext-searchbox';

const { FIRST, SECOND, FIFTH } = INDEX;
const { CREATE_MULESOFT_ENDPOINT } = SPINNER_NAMES;
const { setSelectedTab } = breadCrumbActions;
const { setSelectedIntegrationId } = mulesoftActions;
const { DARK } = ACE_EDITOR_THEMES;

const GenerateFromMulesoft = ({
  setShouldShow = () => {},
  shouldShowBackButton = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { integrationId } = useParams();
  const selectedAlert = useSelector(selectAlert);
  const selectedIntegration = useSelector(selectSelectedIntegration);
  const [xmlInput, setXmlInput] = useState(EMPTY_STRING);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [alertMessage, setAlertMessage] = useState(EMPTY_STRING);
  const [selectedFileName, setSelectedFileName] = useState();
  const [editor, setEditor] = useState();
  const [isCodePasted, setIsCodePasted] = useState(false);

  useEffect(() => {
    if (selectedAlert) setAlertMessage(selectedAlert.message);
  }, [selectedAlert]);

  useEffect(() => {
    if (integrationId) {
      dispatch(setSelectedIntegrationId(integrationId));
      dispatch(setSelectedTab(selectedIntegration));
    }
  }, [integrationId, selectedIntegration, dispatch]);

  useEffect(() => {
    if (isCodePasted) {
      Beautify.beautify(editor.session);
      setIsCodePasted(false);
    }
  }, [isCodePasted, editor]);

  const handleFileUpload = (event) => {
    event.preventDefault();
    setAlertMessage(false);
    const file =
      event.type === "change"
        ? event.target.files[FIRST]
        : event.dataTransfer.files[FIRST];
    setSelectedFileName(file.name);
    if (file && file.type !== "text/xml") {
      setAlertMessage("Invalid file type, please select an XML file.");
    } else {
      setAlertMessage(EMPTY_STRING);
      const reader = new FileReader();
      reader.onloadend = () => {
        const content = reader.result;
        setXmlInput(content);
      };
      reader.readAsText(file);
    }
  };
  const handleDragOver = (event) => event.preventDefault();

  const handleXmlChange = (value) => {
    setXmlInput(value);
    const session = editor.getSession();
    try {
      if (value) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(value.trim(), "text/xml");
        const parsererror = xmlDoc.getElementsByTagName("parsererror");
        if (parsererror.length) {
          const xmlValidationError =
            parsererror[FIRST].innerText.split("\n")[FIRST];
          setAlertMessage(xmlValidationError);
          session.setAnnotations([
            {
              row: xmlValidationError.split(" ")[8] - 1,
              column: 0,
              text: xmlValidationError.split(": ").pop(),
              type: "error",
            },
          ]);
        } else {
          setAlertMessage(EMPTY_STRING);
          session.setAnnotations(EMPTY_ARRAY);
        }
      } else {
        setAlertMessage(EMPTY_STRING);
        session.setAnnotations(EMPTY_ARRAY);
        setSelectedFileName()
      }
    } catch (err) {
      setAlertMessage("Error while validation check.");
    }
  };

  const transformXmlFlowNodes = (xmlInput) => {
    const xmlParser = new DOMParser();
    const xmlDoc = xmlParser.parseFromString(xmlInput, "text/xml");
    const flowNode = xmlDoc.querySelector("flow");
    const childNodes = flowNode.childNodes;
    const fragment = xmlDoc.createDocumentFragment();
    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes[i];
      if (node.nodeName !== "#text") {
        const nodeName = `${node.nodeName}_${i}`;
        const newElement = xmlDoc.createElement(nodeName);
        [...node.attributes].forEach((attr) =>
          newElement.setAttribute(attr.nodeName, attr.nodeValue)
        );
        [...node.childNodes].forEach((child) =>
          newElement.appendChild(child.cloneNode(true))
        );
        fragment.appendChild(newElement);
      }
    }
    flowNode.innerHTML = "";
    flowNode.appendChild(fragment);
    const transformedXmlFlowNodes =  new XMLSerializer().serializeToString(xmlDoc);
    return transformedXmlFlowNodes
  }

  const generateEndpoint = () => {
    const parser = new xml2js.Parser({
      attrkey: false,
      mergeAttrs: true,
      explicitCharkey: true,
      explicitRoot: true,
      explicitArray: false,
    });

    const transformedXmlFlowNodes = transformXmlFlowNodes(xmlInput)

    parser.parseString(transformedXmlFlowNodes, (err, result) => {
      if (err) {
        setAlertMessage("Add proper xml file.");
      } else {
        let json = removeNamespaces(result);
        json = handleCData(json);
        json = JSON.stringify(json, undefined, FIFTH);

        if (Object.is(result, "null")) {
          setAlertMessage("Please upload Xml data.");
        } else {
          dispatch(
            createMulesoftEndpoint({ data: json, id: integrationId })
          ).then((res) => {
            dispatch(fetchMulesoftEndpoints({ id: integrationId }));
            if (res.payload) {
              navigate(`/mulesoft/${integrationId}/endpoints`, {
                replace: true,
              });
              setShouldShow(false);
            } else {
              selectedAlert && setAlertMessage(selectedAlert.message);
            }
          });
        }
      }
    });
  };

  const removeNamespaces = (obj) => {
    if (obj !== null && typeof obj === "object") {
      if (Array.isArray(obj)) {
        return obj.map(removeNamespaces);
      } else {
        const newObj = {};
        for (const key in obj) {
          if (key.includes(":") && typeof obj[key] === "object") {
            const newKey = key.split(":")[SECOND];
            obj[newKey] = obj[key];
            delete obj[key];
            newObj[newKey] = removeNamespaces(obj[newKey]);
          } else {
            newObj[key] = removeNamespaces(obj[key]);
          }
        }
        return newObj;
      }
    }
    return obj;
  };

  const handleCData = (obj) => {
    if (obj !== null && typeof obj === "object") {
      if (Array.isArray(obj)) {
        return obj.map(handleCData);
      } else {
        for (const key in obj) {
          if (key === "_") {
            obj["cdata"] = obj[key];
            delete obj[key];
          } else {
            handleCData(obj[key]);
          }
        }
        return obj;
      }
    }
    return obj;
  };

  return (
    <div className="content scrollbar overflow-auto px-6 flex-1 flex flex-col">
      <div className="flex">
        {shouldShowBackButton && (
          <button
            className="bg-gray-800 h-10 px-2 rounded-md hover:bg-gray-700/60 mr-1"
            onClick={() => setShouldShow(false)}
          >
            <img src={chevronLeftIcon} width="24" alt="chevron-left-icon" />
          </button>
        )}
        <h1
          className={classNames("text-3xl font-bold text-white my-4", {
            "pl-4": shouldShowBackButton,
          })}
        >
          Generate Endpoints from Mulesoft
        </h1>
      </div>
      <div className="flex items-center justify-between mb-2">
        <label
          className={classNames(
            "border-2 border-gray-300 border-dashed rounded-md w-full min-h-[40px] cursor-pointer hover:opacity-90",
            {
              "border-blue-500": selectedFileName,
            }
          )}
          onDragOver={handleDragOver}
          onDrop={handleFileUpload}
        >
          <div className="flex justify-center items-center">
            <div className="icon mb-1 p-1">
              <img src={addImageIcon} width="26" alt="add-icon" />
            </div>
            <div
              className={classNames("text-indigo-200 text-sm", {
                "text-white": selectedFileName,
              })}
            >
              {selectedFileName ||
                `Drag & drop OR click here to upload XML configuration file`}
            </div>
          </div>
          <input
            type="file"
            className="hidden w-2/4"
            value={EMPTY_STRING}
            onChange={handleFileUpload}
            accept=".xml"
          />
        </label>
      </div>

      <div className="flex flex-col h-screen">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-white flex">
            <img
              src={pasteIcon}
              width="20"
              alt="add-icon"
              className="invert mr-2"
            />
            OR paste XML below
          </span>
          <button
            type="button"
            className="btn btn-primary px-8"
            onClick={generateEndpoint}
            disabled={isSpinnerActive || !xmlInput || alertMessage}
          >
            <Spinner
              name={CREATE_MULESOFT_ENDPOINT}
              setIsSpinnerActive={setIsSpinnerActive}
              persistSize
            >
              Generate Endpoints
            </Spinner>
          </button>
        </div>
        {alertMessage && (
          <div className="flex justify-end mb-2">
            <span className="border-2 border-rose-600 rounded-md text-center">
              <p className="text-red-600 text-sm px-2 py-1">
                Error: {alertMessage}
              </p>
            </span>
          </div>
        )}
        <AceEditor
          mode="xml"
          placeholder="Paste XML here..."
          className="bg-gray-800 rounded-md text-gray-300 !h-full"
          theme={DARK}
          name="xml-editor"
          onLoad={setEditor}
          onChange={handleXmlChange}
          enableBasicAutocompletion={true}
          enableLiveAutocompletion={true}
          enableSnippets={false}
          wrapEnabled={true}
          tabSize={2}
          width="100%"
          editorProps={{ $blockScrolling: Infinity }}
          commands={Beautify.commands}
          onPaste={() => setIsCodePasted(true)}
          setOptions={{
            showGutter: true,
            enableLiveAutocompletion: true,
            fontSize: 14,
            highlightActiveLine: true,
            tabSize: 4,
            useWorker: false,
            showPrintMargin: false,
            wrapBehavioursEnabled: true,
          }}
          value={xmlInput}
        />
      </div>
    </div>
  );
};

export default GenerateFromMulesoft;
